import type { StateCreator } from 'zustand'
import type { DocumentRecord, DocumentStats } from '@/types/catalog'
import { apiService, FileMetadata } from '@/service/api'
import type { loadingState } from './loadingSlice'
import type { authSessionState } from './authSessionSlice'
import { toast } from 'sonner'
import { documentFetchManager } from '@/utils/documentFetchManager'
import { adjustStatsForDocument, applyStatusTransitionToStats } from '@/utils/catalogStats'
import { mergeServerDocumentRow, nextSseSettled } from '@/utils/documentRowMerge'
import { i18n } from '@/i18n'

export type DocumentType = 'pnid' | 'pfd' | 'sld' | 'document' | 'datasheet' | 'interview' | 'timeseries' | 'device_config' | 'plc_program' | ''

export interface BoundingBox {
  id: string
  name: string
  x: number
  y: number
  width: number
  height: number
  type: 'text' | 'diagram' | 'table' | 'equipment'
  page?: number
}

export interface DocumentData {
  name: string
  description: string
  type: DocumentType
  file: File | null
  bboxes?: BoundingBox[] | null
  totalPdfPages?: number
  pdfPageNumbers?: number[]
  autoRename?: boolean
  replaceDocumentId?: string
}

export interface UploadDocumentOptions {
  skipToast?: boolean
  signal?: AbortSignal
  autoRename?: boolean
  replaceDocumentId?: string
  uploadStartedAt?: string
  // FS-499: when this upload claims a pre-registered pending row, the
  // backend updates the existing row instead of creating a new one.
  docId?: string
}

export const INITIAL_DOCUMENT_INFO: DocumentData = {
  name: '',
  description: '',
  type: '',
  file: null as File | null,
  bboxes: null,
}

/**
 * How long a locally-created row survives a refetch that does not return it.
 * Mirrors the legacy catalog's stale-temp sweeper (120s): long enough to cover
 * a slow read-after-write, short enough that a row the server genuinely does
 * not have stops being shown.
 */
const OPTIMISTIC_ROW_GRACE_MS = 120_000

export interface DocumentFetchParams {
  page_number?: number
  page_size?: number
  sort_by?: string
  sort_direction?: 'asc' | 'desc'
  search?: string
  type?: string
  user_id?: string
  is_set?: boolean
  /** ISO timestamp — only return docs created on/after this instant. */
  created_after?: string
  /** 'success' | 'failed' | 'processing' — display-status bucket. */
  status_bucket?: string
  /** When true, append results to existing documents instead of replacing */
  append?: boolean
}

export interface dataCatalogState {
  documents: DocumentRecord[]
  totalDocuments: number
  documentStats: DocumentStats | null
  currentFetchParams: DocumentFetchParams
  showAddDocsModal: boolean
  documentInfo: DocumentData
  uploadingFile: boolean
  fetchDocuments: (params?: DocumentFetchParams) => Promise<void>
  updateDocument: (documentId: string, updates: Partial<DocumentRecord>) => void
  /**
   * FS-555: optimistically prepend a freshly-uploaded document so it shows
   * instantly, before the post-upload refetch lands. Restores the V1
   * upload-phase row (FS-38) that the V2 catalog dropped.
   *
   * Dedupe-safe by id: if a row with the same id is already present (e.g. a
   * refetch beat us to it), the stale copy is removed and the fresh row
   * re-prepended (it surfaces at the top) rather than duplicated, and
   * `totalDocuments` is not double-counted. Callers MUST pass the real server
   * document id (returned by the upload response) so the next fetchDocuments()
   * merge reconciles the optimistic row cleanly instead of leaving a ghost.
   */
  prependDocument: (doc: DocumentRecord) => void
  /**
   * Remove a row that only ever existed optimistically — the counterpart to
   * `prependDocument`. An upload row inserted at the START of an upload
   * (FS-499 pre-registration) has to be taken back when the bytes never land,
   * and dropping it locally is not enough on its own: `fetchDocuments` CARRIES
   * FORWARD any un-indexed row the server omits for `OPTIMISTIC_ROW_GRACE_MS`,
   * so a row left in place is re-inserted by the very refetch meant to clear
   * it and sits at "Uploading" for two minutes with nothing behind it.
   *
   * Local only: it does not delete anything server-side. Callers that
   * pre-registered a row pair this with `/v2/files/discard-pending`.
   */
  removeDocument: (documentId: string) => void
  uploadDocument: (
    file: File,
    metadata: FileMetadata,
    options?: UploadDocumentOptions
  // Returns the created document so a caller can show an optimistic row keyed
  // on the REAL server id (FS-555). Widened from `void`; existing callers that
  // ignore the result are unaffected.
  ) => Promise<{ id?: string } | void>
  deleteDocuments: (docIds: string[]) => Promise<void>
  setShowAddDocsModal: (isOpen?: boolean) => void
  setDocumentInfo: (info: DocumentData) => void
  resetDocumentInfo: () => void
  setUploadingFile: (isUploading: boolean) => void
}

const messages = {
  success: {
    fetchDocuments: 'Documents fetched successfully',
    uploadDocument: 'Document uploaded successfully',
    deleteDocuments: 'Documents deleted successfully',
  },
  error: {
    fetchDocuments: 'Error fetching documents',
    uploadDocument: 'Error uploading document',
    deleteDocuments: 'Error deleting documents',
    uploadTimeout: 'Upload timed out. Please try again with a smaller file or check your connection.',
    uploadFileTooLarge: 'File is too large. Maximum size is 512MB.',
    uploadNetworkError: 'Network error during upload. Please check your connection.',
  },
}

/**
 * Extended Error type that includes HTTP response information
 * This matches the error structure created by fetchOrError() in api.ts
 */
interface ErrorWithResponse extends Error {
  response?: {
    status: number
    statusText: string
    data: string
  }
}

/**
 * Determines if an error is retryable (network issues, timeouts)
 * Non-retryable errors: 4xx client errors, file too large, validation errors
 */
const isRetryableError = (error: unknown): boolean => {
  if (error instanceof Error) {
    // Don't retry on user abort
    if (error.name === 'AbortError') return false

    // Check actual HTTP status code from response (preferred method)
    const errWithResponse = error as ErrorWithResponse
    const status = errWithResponse.response?.status
    if (status) {
      // Don't retry on client errors (4xx) - these are permanent failures
      if (status >= 400 && status < 500) return false
      // Retry on server errors (5xx) - these may be transient
      if (status >= 500) return true
    }

    // Fallback to message checking for network-level errors (no HTTP status)
    const message = error.message.toLowerCase()
    // Retry on network errors and timeouts
    if (error.name === 'TypeError') return true // Network failure
    if (message.includes('timeout')) return true
    if (message.includes('network')) return true
    if (message.includes('failed to fetch')) return true
  }
  return false
}

/**
 * Extended Error type that includes error_code from backend
 */
interface ErrorWithCode extends Error {
  error_code?: string
}

/**
 * Get a user-friendly error message based on the error type
 * Uses error codes for robust error handling and i18n support
 */
const getUploadErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    const errorWithCode = error as ErrorWithCode & ErrorWithResponse

    // Check actual HTTP status code from response (preferred method)
    const status = errorWithCode.response?.status

    if (status === 413) {
      return messages.error.uploadFileTooLarge
    }
    if (status === 408) {
      return messages.error.uploadTimeout
    }

    // Check for structured error_code from backend (robust approach)
    const errorCode = errorWithCode.error_code

    if (errorCode) {
      // Map error codes to i18n keys
      switch (errorCode) {
        case 'CSV_ENCODING_ERROR': {
          const baseMsg = i18n.t('data_catalog:upload_modal.csv_errors.encoding_error')
          const helpMsg = i18n.t('data_catalog:upload_modal.csv_errors.encoding_help')
          return `${baseMsg} ${helpMsg}`
        }

        case 'CSV_NO_DATA_COLUMNS':
          return i18n.t('data_catalog:upload_modal.csv_errors.no_data_columns')

        case 'CSV_NO_VALID_ROWS':
          return i18n.t('data_catalog:upload_modal.csv_errors.no_valid_rows')

        case 'CSV_NO_NUMERIC_VALUES':
          return i18n.t('data_catalog:upload_modal.csv_errors.no_numeric_values')

        case 'CSV_VALIDATION_ERROR':
          return i18n.t('data_catalog:upload_modal.csv_errors.validation_failed')

        default:
          // Unknown error code, check if it's CSV-related
          if (errorCode.startsWith('CSV_')) {
            return i18n.t('data_catalog:upload_modal.csv_errors.processing_error')
          }
      }
    }

    // Fallback: Check message content for errors without codes (legacy/network errors)
    const message = error.message.toLowerCase()

    if (message.includes('timeout')) {
      return messages.error.uploadTimeout
    }
    if (error.name === 'TypeError' || message.includes('network') || message.includes('failed to fetch')) {
      return messages.error.uploadNetworkError
    }

    // Return the original error message if it's specific and user-friendly
    // (not a generic system error, and not too long)
    if (error.message && error.message.length < 200 && !message.includes('undefined') && !message.includes('null')) {
      return error.message
    }
  }
  return messages.error.uploadDocument
}

/**
 * Retry a function with exponential backoff
 * @param fn - Function to retry
 * @param maxRetries - Maximum number of retries (default: 3)
 * @param baseDelay - Base delay in ms (default: 1000, will double each retry)
 * @param onRetry - Optional callback when a retry occurs
 */
const retryWithBackoff = async <T,>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000,
  onRetry?: (attempt: number, error: Error) => void
): Promise<T> => {
  // Ensure maxRetries is non-negative
  const safeMaxRetries = Math.max(0, maxRetries)
  let lastError: Error | undefined
  for (let attempt = 0; attempt <= safeMaxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error))

      // Don't retry if this is a non-retryable error or we've exhausted retries
      if (!isRetryableError(error) || attempt === safeMaxRetries) {
        throw error
      }

      // Exponential backoff: 1s, 2s, 4s
      const delay = baseDelay * Math.pow(2, attempt)

      if (onRetry) {
        onRetry(attempt + 1, lastError)
      }

      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
  throw lastError || new Error('Retry failed')
}

/**
 * Mirrors MAX_PAGE_SIZE in documents.controller.ts — the server clamps
 * `page_size` to this, silently. A refresh must not ask for more than it can
 * get back, or the response would replace the list with a truncated window.
 */
const MAX_PAGE_SIZE = 500

/**
 * Build the params for a "refresh the current view" fetch (no explicit params)
 * out of the last-used ones.
 *
 * `currentFetchParams` remembers the LAST fetch, and after any infinite-scroll
 * load that is `{ page_number: N, append: true }`. Replaying it verbatim appends
 * page N onto the list a second time instead of refreshing, so rows the server
 * no longer returns — the ones just deleted — stay on screen, while the header
 * stats (always taken from the response) drop to the real count. That mismatch
 * is the "deleted files still listed" bug.
 *
 * Only the infinite-scroll view accumulates pages into one list, so only it gets
 * rewritten: drop `append` and collapse the pages scrolled through into a single
 * replace-fetch covering the same window (pages are 0-indexed). A classic
 * paginated view — the legacy catalog, which sends `page_number` with no
 * `append` — is already a replace-fetch of the page it is showing, and must
 * refresh THAT page; rewriting it to page 0 would render the first page's rows
 * under a paginator still reporting page N.
 *
 * Load-bearing invariant: a refresh must NOT write its result back into
 * `currentFetchParams` (which is why `fetchDocuments` only persists params it was
 * explicitly given). Consecutive no-arg refreshes therefore compute identical
 * params and collapse into one request under documentFetchManager's dedupe. If a
 * refresh ever persisted its own params, each one would widen the window off the
 * previous one, defeat the dedupe, and grow page_size until it hit the cap.
 */
const refreshParamsFor = (last: DocumentFetchParams): DocumentFetchParams => {
  if (last.append !== true) return last

  const { append: _append, page_number, page_size, ...view } = last
  const pagesLoaded = (page_number ?? 0) + 1
  return {
    ...view,
    page_number: 0,
    // Leave page_size to the server default when the last fetch didn't set one.
    // Past the cap the window shrinks to 500 rather than being silently
    // truncated by the server; `hasMore` stays true, so the rest re-loads on
    // scroll instead of vanishing under a total that still counts them.
    ...(page_size != null ? { page_size: Math.min(pagesLoaded * page_size, MAX_PAGE_SIZE) } : {}),
  }
}

export const dataCatalogSlice: StateCreator<
  dataCatalogState & loadingState & authSessionState,
  [],
  [],
  dataCatalogState
> = (set, get) => ({
  documents: [],
  totalDocuments: 0,
  documentStats: null,
  currentFetchParams: {},
  showAddDocsModal: false,
  documentInfo: INITIAL_DOCUMENT_INFO,
  uploadingFile: false,

  setDocumentInfo: (info: DocumentData) => set({ documentInfo: info }),

  resetDocumentInfo: () =>
    set({
      documentInfo: INITIAL_DOCUMENT_INFO,
    }),

  fetchDocuments: async (params?: DocumentFetchParams) => {
    const { setLoading, currentFetchParams } = get()

    // Use provided params, or rebuild a refresh of the current view from the
    // last-used params (post-delete, post-upload, SSE-triggered refetches).
    const fetchParams = params ?? refreshParamsFor(currentFetchParams)
    const shouldAppend = fetchParams.append === true

    // Strip `append` before sending to API (not a server param)
    const { append: _append, ...apiParams } = fetchParams

    try {
      // Only show full loading state for non-append fetches (append uses its own loadingMore)
      if (!shouldAppend) {
        setLoading(true)
      }

      // Store the params so SSE-triggered refetches use the same view
      if (params) {
        set({ currentFetchParams: params })
      }

      const response = await documentFetchManager.fetch(
        (signal) => apiService.listDocsPaginated(apiParams, signal),
        apiParams as Record<string, unknown>,
        // FS-601: a refresh of the current view must read current state. Without
        // this, the manager hands back an already-in-flight response for the same
        // params — so on a multi-sheet drawing every per-page refetch collapses
        // onto ONE snapshot taken at the first page's completion, and the row is
        // written from a payload that can be minutes old.
        { fresh: params === undefined }
      )

      // Merge server data over existing SSE-driven state. The server is the
      // source of truth for is_indexed/status EXCEPT where this response
      // predates a completion SSE already delivered, and processing_metadata is
      // frontend-only — see utils/documentRowMerge.ts. Without the merge, a
      // concurrent fetchDocuments() both drops processing_metadata (making
      // hasProcessingDocuments miscalculate and potentially disconnecting SSE
      // before all events arrive) and reverts finished rows to "Processing".
      //
      // FS-601: read the rows AFTER the await, not from a pre-request closure.
      // The whole point is that SSE patches land while this request is in
      // flight; comparing against the state as it was when the request was
      // issued would miss exactly the completion we need to protect.
      const { documents: existingDocuments } = get()
      const existingMap = new Map(existingDocuments.map((doc) => [doc.id, doc]))
      const mergedData = response.documents.map((serverDoc) =>
        mergeServerDocumentRow(serverDoc, existingMap.get(serverDoc.id))
      )

      const documentStats = response.stats ?? null

      if (shouldAppend) {
        // Append mode: add new docs to existing list, deduplicate by id
        const existingIds = new Set(existingDocuments.map((d) => d.id))
        const newDocs = mergedData.filter((d) => !existingIds.has(d.id))
        set({ documents: [...existingDocuments, ...newDocs], totalDocuments: response.total, documentStats })
      } else {
        // Carry forward a just-uploaded row the server has not surfaced yet.
        //
        // This branch REPLACES the list with the response, so without this the
        // post-upload refetch deletes the optimistic row in exactly the case it
        // exists for (FS-555: the refetch "can fail to surface it"). Losing it
        // leaves no processing row at all, and because the SSE gate is derived
        // from `documents`, the stream then never opens — so the row cannot
        // recover on its own either.
        //
        // Deliberately narrow, because keeping a row the server did not return
        // is how deleted rows get resurrected:
        //   - only rows still within the grace window, so a row the server
        //     keeps omitting disappears on the next refetch rather than
        //     sticking forever;
        //   - only un-indexed rows, so nothing finished is ever revived;
        //   - only on an unfiltered view, so a carried row can never contradict
        //     an active search/type/status filter.
        const returnedIds = new Set(mergedData.map((d) => d.id))
        const viewIsUnfiltered =
          !apiParams.search &&
          !apiParams.type &&
          !apiParams.user_id &&
          !apiParams.status_bucket &&
          !apiParams.created_after
        const cutoff = Date.now() - OPTIMISTIC_ROW_GRACE_MS
        const pendingLocalRows = viewIsUnfiltered
          ? existingDocuments.filter(
              (doc) =>
                !returnedIds.has(doc.id) &&
                !doc.is_indexed &&
                !!doc.upload_started_at &&
                new Date(doc.upload_started_at).getTime() >= cutoff
            )
          : []

        set({
          documents: [...pendingLocalRows, ...mergedData],
          totalDocuments: response.total,
          documentStats,
        })
      }
    } catch (e) {
      // Ignore AbortError - it's expected when requests are superseded
      if (e instanceof Error && e.name !== 'AbortError') {
        await toast.error(messages.error.fetchDocuments)
      }
      // Don't clear documents on AbortError to prevent flickering
      if (!(e instanceof Error && e.name === 'AbortError')) {
        set({ documents: [], totalDocuments: 0 })
      }
    } finally {
      if (!shouldAppend) {
        setLoading(false)
      }
    }
  },

  updateDocument: (documentId: string, updates: Partial<DocumentRecord>) => {
    const { documents, documentStats } = get()
    const oldDoc = documents.find((d) => d.id === documentId)
    // FS-601: a patch that carries status information also records whether SSE
    // has now settled this row, so a later stale refetch can't undo it. Patches
    // that carry neither field (a rename) leave the flag alone.
    const carriesStatus = 'status' in updates || 'is_indexed' in updates
    const patch: Partial<DocumentRecord> =
      oldDoc && carriesStatus
        ? { ...updates, sse_settled: nextSseSettled(oldDoc, { ...oldDoc, ...updates }) }
        : updates
    set({
      documents: documents.map((doc) =>
        doc.id === documentId ? { ...doc, ...patch } : doc
      ),
      // FS-603: an SSE status change (e.g. processing → success/failed) must
      // also move the row between banner buckets, else the header count lags
      // the row's status pill until the next full refetch.
      documentStats: oldDoc
        ? applyStatusTransitionToStats(documentStats, oldDoc, { ...oldDoc, ...patch })
        : documentStats,
    })
  },

  prependDocument: (doc: DocumentRecord) => {
    const { documents, totalDocuments, documentStats } = get()
    // Dedupe by id: a concurrent fetchDocuments()/SSE event may have already
    // landed this row. Remove the stale copy and re-prepend the fresh one so it
    // surfaces at the top; only bump the count for a genuinely new row.
    const alreadyPresent = documents.some((d) => d.id === doc.id)
    const rest = alreadyPresent ? documents.filter((d) => d.id !== doc.id) : documents
    set({
      documents: [doc, ...rest],
      totalDocuments: alreadyPresent ? totalDocuments : totalDocuments + 1,
      // FS-603: a genuinely new optimistic row (uploads land as `processing`)
      // must also count into the banner aggregate, else the header lags the
      // just-inserted "Processing" row until the post-upload refetch. When the
      // row is already present the authoritative fetch that landed it already
      // counted it, so leave the aggregate alone.
      documentStats: alreadyPresent ? documentStats : adjustStatsForDocument(documentStats, doc, 1),
    })
  },

  removeDocument: (documentId: string) => {
    const { documents, totalDocuments, documentStats } = get()
    const doomed = documents.find((d) => d.id === documentId)
    if (!doomed) return
    set({
      documents: documents.filter((d) => d.id !== documentId),
      // Never below zero: the row may have been counted by an authoritative
      // fetch rather than by `prependDocument`, and a negative total renders
      // as a nonsense "-1 files" header.
      totalDocuments: Math.max(0, totalDocuments - 1),
      documentStats: adjustStatsForDocument(documentStats, doomed, -1),
    })
  },

  uploadDocument: async (
    file: File,
    metadata: FileMetadata,
    options?: UploadDocumentOptions
  ) => {
    const { setLoading, fetchDocuments, setUploadingFile } = get()

    if (!file) {
      return
    }

    setLoading(true)
    setUploadingFile(true)

    try {
      const data = await retryWithBackoff(
        () => apiService.uploadFile(file, metadata, options?.signal, {
          autoRename: options?.autoRename,
          replaceDocumentId: options?.replaceDocumentId,
          uploadStartedAt: options?.uploadStartedAt,
          docId: options?.docId,
        })
      )

      if (data.error) {
        throw new Error(data.error)
      }

      // Fire-and-forget fetchDocuments() to avoid blocking subsequent uploads
      // The debounce/deduplication in fetchDocuments will handle multiple rapid calls
      fetchDocuments().catch(() => {
        // Ignore fetch errors - they're logged separately
      })
      // Success toast removed - status column already shows upload success
      return data
    } catch (error) {
      console.error(messages.error.uploadDocument, error)
      if (!options?.skipToast) {
        await toast.error(getUploadErrorMessage(error))
      }
      throw error // Re-throw so callers can handle it (e.g., bulk upload error tracking)
    } finally {
      setLoading(false)
      setUploadingFile(false)
    }
  },

  deleteDocuments: async (docIds: string[]) => {
    const { setLoading, fetchDocuments } = get()

    setLoading(true)

    try {
      await apiService.deleteDocuments(docIds)
    } catch (error) {
      console.error(messages.error.deleteDocuments, error)
      throw error
    } finally {
      setLoading(false)
      // Always refresh — even after a failed delete — so the catalog never keeps
      // showing documents that were already removed server-side, and a failed
      // delete (e.g. a live_stream/cross-tenant rejection) doesn't leave a stale
      // list behind (FS-600). A failed refresh shouldn't surface as a delete
      // error, so swallow it.
      fetchDocuments().catch((err) =>
        console.warn('Failed to refresh documents after delete:', err)
      )
    }
  },

  setShowAddDocsModal: (isOpen: boolean = true) =>
    set({ showAddDocsModal: isOpen }),

  setUploadingFile: (isUploading: boolean) =>
    set({ uploadingFile: isUploading }),
})
